// ============================================================
// ✅ src/components/permissions/TenantRolePermissionMatrix.js
// Final Enhanced Version – Updated 2025-11-04
// ------------------------------------------------------------
// ✨ Features:
//  • Gradient chips + Gradient buttons
//  • "Select All" per column (View/Create/Edit/Delete/Approve)
//  • Green color for checked boxes
//  • Rows-per-page selector + Sorting + Filtering
// ============================================================

import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Spinner } from "react-bootstrap";
import axiosClient from "../../api/axiosClient";
import "../../styles/UserForm.css";
import { MdArrowDropUp, MdArrowDropDown } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../styles/SelectFix.css";

export default function TenantRolePermissionMatrix({ tenantId, onClose }) {
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("module");
  const [sortDir, setSortDir] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // ==========================================================
  // 🎨 Select Styles (gradient chip + high z-index)
  // ==========================================================
  const selectStyles = {
    container: (base) => ({
      ...base,
      zIndex: 10,
      minWidth: 220,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      position: "absolute",
      background: "white",
    }),
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      borderColor: state.isFocused ? "#007bff" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 1px #007bff" : "none",
      "&:hover": { borderColor: "#007bff" },
    }),
    multiValue: (base) => ({
      ...base,
      background: "linear-gradient(90deg, #007bff, #6f42c1)",
      color: "white",
      borderRadius: "12px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "rgba(255,255,255,0.2)",
        color: "#fff",
      },
    }),
  };

  // ==========================================================
  // 🔧 Helpers
  // ==========================================================
  const safeString = (v) => (v == null ? "" : String(v));
  const sortList = (list, key, dir) =>
    [...list].sort((a, b) => {
      const valA = safeString(a?.[key]);
      const valB = safeString(b?.[key]);
      return dir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => (
    <span className={`sort-icon ${sortKey === col ? "sort-active" : ""}`}>
      {sortKey === col ? (
        sortDir === "asc" ? (
          <MdArrowDropUp />
        ) : (
          <MdArrowDropDown />
        )
      ) : (
        <MdArrowDropDown style={{ opacity: 0.3 }} />
      )}
    </span>
  );

  // ==========================================================
  // 🧭 Load Roles & Permissions
  // ==========================================================
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // 🔹 Fetch roles
        const roleRes = await axiosClient.get(`/tenant/api/roles`, {
          params: { tenant_id: tenantId },
        });
        const formattedRoles = (roleRes?.data?.data || []).map((r) => ({
          value: r.id,
          label: r.name,
        }));
        setRoles(formattedRoles);

        // 🔹 Fetch permissions
        const permRes = await axiosClient.get(`/admin/permissions/tenant/list`, {
          params: { tenant_id: tenantId },
        });
        const permData = permRes?.data?.data || [];
        setPermissions(permData);

        // 🔹 Build unique module list
        const uniqueModules = [
          ...new Set(permData.map((p) => safeString(p.module))),
        ]
          .filter((m) => m)
          .map((m) => ({ value: m, label: m }));
        setModules(uniqueModules);
      } catch (err) {
        console.error("❌ Failed to load tenant permissions:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [tenantId]);

  // ==========================================================
  // ✏️ Toggle checkbox
  // ==========================================================
  const handleToggle = (roleId, funcId, field) => {
    setPermissions((prev) =>
      prev.map((p) =>
        p.role_id === roleId && p.functionality_id === funcId
          ? { ...p, [field]: !p[field] }
          : p
      )
    );
  };

  // ==========================================================
  // 💾 Save Changes
  // ==========================================================
  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = permissions.map((p) => ({
        role_id: p.role_id,
        functionality_id: p.functionality_id,
        can_view: !!p.can_view,
        can_create: !!p.can_create,
        can_edit: !!p.can_edit,
        can_delete: !!p.can_delete,
        can_approve: !!p.can_approve,
      }));

      await axiosClient.put(`/admin/permissions/tenant/update/${tenantId}`, payload);

      toast.success("Tenant permissions updated successfully!");

      const permRes = await axiosClient.get(
        `/admin/permissions/tenant/list?tenant_id=${tenantId}`
      );
      const permData = permRes?.data?.data || [];
      setPermissions(permData);
    } catch (err) {
      console.error("❌ Failed to save tenant permissions:", err);
      toast.error("Failed to save tenant permissions.");
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================
  // 🔍 Filter + Pagination
  // ==========================================================
  const filtered = sortList(
    permissions.filter((p) => {
      const q = search.toLowerCase();
      const searchMatch =
        safeString(p.role_name).toLowerCase().includes(q) ||
        safeString(p.functionality_name).toLowerCase().includes(q) ||
        safeString(p.module).toLowerCase().includes(q);
      const roleMatch =
        selectedRoles.length === 0 ||
        selectedRoles.some((r) => r.value === p.role_id);
      const moduleMatch =
        selectedModules.length === 0 ||
        selectedModules.some(
          (m) =>
            m.value.toLowerCase() === safeString(p.module).toLowerCase()
        );
      return searchMatch && roleMatch && moduleMatch;
    }),
    sortKey,
    sortDir
  );

  const totalEntries = filtered.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage) || 1;
  const startIdx = (currentPage - 1) * rowsPerPage;
  const currentRows = filtered.slice(startIdx, startIdx + rowsPerPage);

  // ==========================================================
  // 🖥️ UI
  // ==========================================================
  return (
    <div className="drawer-panel-inner container-fluid p-0">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div
        className="users-hero shadow-sm d-flex justify-content-between align-items-center flex-wrap px-3"
        style={{
          background: "linear-gradient(90deg, #007bff, #6f42c1)",
          color: "white",
        }}
      >
        <div>
          <div className="h5 fw-bold mb-1 text-white">
            Tenant Role Permission Matrix
          </div>
          <div className="text-white-50 small">
            All roles & permissions for Tenant ID: {tenantId}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            className="fw-semibold shadow-sm text-white border-0 px-3 py-2"
            style={{
              background: "linear-gradient(90deg, #007bff, #6f42c1)",
              borderRadius: "8px",
            }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              <>💾 Save Changes</>
            )}
          </button>

          <button
            className="fw-semibold shadow-sm text-white border-0 px-3 py-2"
            style={{
              background: "linear-gradient(90deg, #6f42c1, #007bff)",
              borderRadius: "8px",
            }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="d-flex flex-wrap gap-2 align-items-center mt-3 px-3">
        <div style={{ minWidth: 220 }}>
          <Select
            isMulti
            options={roles}
            value={selectedRoles}
            onChange={setSelectedRoles}
            placeholder="Filter by role(s)..."
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div style={{ minWidth: 220 }}>
          <Select
            isMulti
            options={modules}
            value={selectedModules}
            onChange={setSelectedModules}
            placeholder="Filter by module(s)..."
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 280 }}
          placeholder="Search Role, Functionality, Code or Type..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="card shadow-sm mt-3 mx-3 mb-3 table-responsive">
        {loading ? (
          <div className="text-center p-4">Loading permissions...</div>
        ) : currentRows.length === 0 ? (
          <div className="text-center p-4 text-muted">No data available.</div>
        ) : (
          <table className="table table-sm users-table mb-0">
            <thead className="table-light">
              <tr>
                <th onClick={() => toggleSort("role_name")}>
                  Role <SortIcon col="role_name" />
                </th>
                <th onClick={() => toggleSort("functionality_name")}>
                  Functionality <SortIcon col="functionality_name" />
                </th>
                <th onClick={() => toggleSort("module")}>
                  Module <SortIcon col="module" />
                </th>

                {/* Select All Header Checkboxes */}
                {[
                  { key: "can_view", label: "View" },
                  { key: "can_create", label: "Create" },
                  { key: "can_edit", label: "Edit" },
                  { key: "can_delete", label: "Delete" },
                  { key: "can_approve", label: "Approve" },
                ].map(({ key, label }) => {
                  const allChecked = currentRows.every((p) => p[key]);
                  const someChecked =
                    !allChecked && currentRows.some((p) => !!p[key]);

                  return (
                    <th key={key} className="text-center">
                      <div className="d-flex flex-column align-items-center">
                        <span>{label}</span>
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => {
                            if (el) el.indeterminate = someChecked;
                          }}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPermissions((prev) =>
                              prev.map((p) =>
                                currentRows.some(
                                  (r) =>
                                    r.role_id === p.role_id &&
                                    r.functionality_id === p.functionality_id
                                )
                                  ? { ...p, [key]: checked }
                                  : p
                              )
                            );
                          }}
                          style={{
                            accentColor: "green",
                            transform: "scale(1.2)",
                          }}
                        />
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {currentRows.map((p) => (
                <tr key={`${p.role_id}-${p.functionality_id}`}>
                  <td>{p.role_name}</td>
                  <td>{p.functionality_name}</td>
                  <td>{p.module}</td>
                  {[
                    "can_view",
                    "can_create",
                    "can_edit",
                    "can_delete",
                    "can_approve",
                  ].map((field) => (
                    <td key={field} className="text-center">
                      <input
                        type="checkbox"
                        checked={!!p[field]}
                        onChange={() =>
                          handleToggle(p.role_id, p.functionality_id, field)
                        }
                        style={{
                          accentColor: p[field] ? "green" : "#ccc",
                          transform: "scale(1.2)",
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination + Rows per Page */}
        {!loading && currentRows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center p-2 flex-wrap small text-muted">
            <div className="d-flex align-items-center gap-2">
              <span>Rows per page:</span>
              <select
                className="form-select form-select-sm"
                value={rowsPerPage}
                style={{ width: "70px" }}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[5, 10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div>
              Showing {startIdx + 1}–
              {Math.min(startIdx + rowsPerPage, totalEntries)} of {totalEntries}{" "}
              entries
            </div>

            <div className="d-flex gap-1">
              <button
                className="btn btn-sm btn-light"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                ‹
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`btn btn-sm ${
                    currentPage === i + 1 ? "btn-primary text-white" : "btn-light"
                  }`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="btn btn-sm btn-light"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
